'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import HIHImage from './HIHImage';
import Tag from './Tag';
import HIHLink from './Link';
import { URL_PATH_ARTICLE, URL_PATH_CATEGORY } from '@/shared/constants';
import {
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_CONTENT_KINDS,
  buildContentPiece,
} from '@/utils/matomo';
import { useContentImpression } from '@/hooks/useContentImpression';
import { ContentBlockProvider } from './ContentTracking';
import styles from './SingleArticleMobileFullviewCard.module.scss';
import ActionFavorite from '../action/favorite/ActionFavorite';

interface SingleArticleCardProps {
  article: ArticleCard;
  variant?: 'primary' | 'secondary';
}

export default function SingleArticleMobileFullViewCard({
  article,
  variant = 'primary',
}: SingleArticleCardProps) {
  const categories = article.categories?.slice(0, 3);
  const articlePath = `/${URL_PATH_ARTICLE}/${article.slug?.current}`;

  // Trackt nur innerhalb eines ContentTrackingModule, sonst No-Op – siehe ContentTracking.
  const { ref, block, trackInteraction } = useContentImpression({
    contentPiece: buildContentPiece(MATOMO_CONTENT_KINDS.GUIDE, article.slug?.current),
    contentTarget: articlePath,
  });

  const handleOpenClick = () =>
    trackInteraction(MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET);

  return (
    <ContentBlockProvider block={block}>
      <div
        ref={ref}
        className={[
          styles.singleArticleMobileFullviewCard,
          variant === 'primary' ? styles.primary : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <HIHLink
          key={article._id}
          href={articlePath}
          onClick={handleOpenClick}
          className={[
            styles.imageLink,
            variant === 'primary' ? styles.primaryImage : styles.secondaryImage,
          ].join(' ')}
        >
          <HIHImage
            image={article.coverImage}
            fill
            sizes="(min-width: 1024px) 50vw, 100vw"
            className={styles.image}
          />
        </HIHLink>
        <ActionFavorite
          id={article._id}
          className={styles.favoriteButton}
          bookmarkType="Guide"
        ></ActionFavorite>
        <div
          className={[
            styles.body,
            variant === 'primary' ? styles.primaryBody : styles.secondaryBody,
          ].join(' ')}
        >
          <div className={styles.tags}>
            {categories?.map((category) => (
              <Tag
                key={category._id}
                label={category.name}
                size="sm"
                hasHoverEffect={false}
                href={`/${URL_PATH_CATEGORY}/${category.slug?.current}`}
              />
            ))}
          </div>
          <HIHLink key={article._id} href={articlePath} onClick={handleOpenClick}>
            <div className={styles.articleContent}>
              <div className={styles.title}>{article.title}</div>
              <div className={styles.description}>{article.shortDescription}</div>
            </div>
          </HIHLink>
        </div>
      </div>
    </ContentBlockProvider>
  );
}
